"use client";

import * as React from "react";
import dynamic from "next/dynamic";
import { SceneBoundary, canRun3d } from "@/components/3d/lazy-scene";
import { usePrefersReducedMotion } from "@/lib/use-reduced-motion";
import LogoMark from "./LogoMark";

// three.js só é baixado depois que a página carregou (o texto do hero pinta antes).
const LogoScene = dynamic(() => import("@/components/3d/hero/LogoScene"), {
  ssr: false,
  loading: () => null,
});

/**
 * Logo Alert AI em 3D no centro da constelação. Até a cena carregar (ou sem WebGL /
 * "economia de dados") aparece o logo em SVG.
 */
export default function HeroLogo({ className }: { className?: string }) {
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const reduced = usePrefersReducedMotion();
  const [enable3d, setEnable3d] = React.useState(false);
  const [visible, setVisible] = React.useState(true);
  const [ready, setReady] = React.useState(false);
  const [showFallback, setShowFallback] = React.useState(true);

  // Three.js só entra depois que a janela termina de carregar (fontes, imagens,
  // scripts): a extrusão do logo + o ambiente PMREM custam uma tarefa longa só
  // de main thread, e disparar isso durante a hidratação inicial é o que inflava
  // o Total Blocking Time no Lighthouse. Adiar para depois do `load` mantém o
  // mesmo crossfade do SVG para o 3D — só começa um pouco mais tarde.
  React.useEffect(() => {
    if (!canRun3d()) return;
    // Safari ainda não implementa requestIdleCallback apesar do tipo do DOM
    // não marcá-lo opcional — checa em runtime, não só no tipo.
    const hasIdleCallback = typeof window.requestIdleCallback === "function";
    let idleId: number | undefined;
    let timeoutId: number | undefined;

    const schedule = () => {
      const start = () => setEnable3d(true);
      if (hasIdleCallback) {
        idleId = window.requestIdleCallback(start, { timeout: 2500 });
      } else {
        timeoutId = window.setTimeout(start, 1200);
      }
    };

    if (document.readyState === "complete") {
      schedule();
    } else {
      window.addEventListener("load", schedule, { once: true });
    }

    return () => {
      window.removeEventListener("load", schedule);
      if (idleId !== undefined && hasIdleCallback) window.cancelIdleCallback(idleId);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, []);

  // pausa o desenho quando o hero sai da tela
  React.useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { threshold: 0.05 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  React.useEffect(() => {
    if (!ready) return;
    const id = window.setTimeout(() => setShowFallback(false), 900);
    return () => window.clearTimeout(id);
  }, [ready]);

  return (
    <div ref={wrapRef} aria-hidden="true" className={className}>
      {/* brilho atrás do logo (fora do canvas, sem cortes retos) */}
      <div
        className="absolute -inset-[32%]"
        style={{
          background:
            "radial-gradient(closest-side, rgb(43 155 255 / 0.34), rgb(43 155 255 / 0.1) 55%, transparent 100%)",
        }}
      />
      {showFallback && (
        <div
          className="absolute inset-0 grid place-items-center transition-opacity duration-700"
          style={{ opacity: ready ? 0 : 1 }}
        >
          <LogoMark className="w-[58%] animate-float" />
        </div>
      )}
      {enable3d && (
        <div
          className="absolute inset-0 transition-opacity duration-700"
          style={{ opacity: ready ? 1 : 0 }}
        >
          <SceneBoundary onError={() => setEnable3d(false)}>
            <LogoScene active={visible} reduced={reduced} onReady={() => setReady(true)} />
          </SceneBoundary>
        </div>
      )}
    </div>
  );
}
